import { type Breakpoint, useBreakpoint } from '@vizit/hooks';
import { useThumbnailColumns } from '@vizit/molecules';
import * as React from 'react';
import { UploadQueueContext } from '../upload-queue';

/** Below `lg` even the largest thumbnails get too narrow for a fixed column count, so the grid stays responsive (D25). */
const RESPONSIVE_ONLY_BREAKPOINTS = new Set<Breakpoint | 'base'>(['base', 'sm', 'md']);

interface UseThumbnailSliderOptions {
  /**
   * Whether the page offers the slider at all, e.g. while its feature flag is on.
   *
   * @required
   */
  isEnabled: boolean;
  /**
   * Hides the slider while something else needs the bottom of the page, e.g. the selection action bar. The grid keeps
   * the picked size meanwhile.
   *
   * @optional
   * @default false
   */
  isHidden?: boolean;
}

interface ThumbnailSlider {
  /** Images per row for the grid. `undefined` keeps the responsive columns (small screens, slider off). */
  gridColumns: number | undefined;
  /** The picked images per row, for `ThumbnailSizeSlider`. */
  columns: number;
  setColumns: (columns: number) => void;
  /** Whether to render `ThumbnailSizeSlider` and make room for it under the grid. */
  isVisible: boolean;
}

/**
 * The thumbnail size slider of an image grid page (Collection, Assets, later Benchmark): the size remembered per grid
 * `id`, and when the slider shows. One place for the rules, so the pages can't drift apart:
 * - below `lg` there is no slider and the grid stays responsive (D25)
 * - the slider steps aside while the upload queue is open, it opens in the same bottom-right corner
 * - the page can hide it for its own reasons with `isHidden`
 *
 * The upload queue is read from its context directly: `useUploadQueue` throws outside the provider, and a page
 * without a queue simply has nothing uploading.
 *
 * @example
 * const slider = useThumbnailSlider('collection', { isEnabled: showActionsBar, isHidden: isSelectionMode });
 * <AssetGrid columns={slider.gridColumns} />
 * {slider.isVisible && <ThumbnailSizeSlider columns={slider.columns} onColumnsChange={slider.setColumns} />}
 */
function useThumbnailSlider(id: string, options: UseThumbnailSliderOptions): ThumbnailSlider {
  const { isEnabled, isHidden = false } = options;
  const [columns, setColumns] = useThumbnailColumns(id);
  const breakpoint = useBreakpoint();
  const isUploadQueueOpen = (React.useContext(UploadQueueContext)?.items.length ?? 0) > 0;

  const isActive = isEnabled && !RESPONSIVE_ONLY_BREAKPOINTS.has(breakpoint);
  const gridColumns = isActive ? columns : undefined;
  const isVisible = isActive && !isUploadQueueOpen && !isHidden;

  // Stable between renders, pages pass it to memoized panels
  return React.useMemo(
    () => ({ gridColumns, columns, setColumns, isVisible }),
    [gridColumns, columns, setColumns, isVisible],
  );
}

export { useThumbnailSlider };
export type { ThumbnailSlider, UseThumbnailSliderOptions };
